import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import Proptypes from "prop-types";

import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import ErrorModal from "@meetyoo/components/src/components/Modal/ErrorModal";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";

import { sendingDocumentNumbers, sendingDocumentNumberOnhold, resetFailures } from "../../../actions/billing";

import { useDefaultPasscodeStyles } from "../../../constants/passCodeUtils";

const BillingDocumentSendingEventComponent = ({
  documentNumbers,
  translate,
  sendingDocumentNumbersFn,
  setDocumentNumberOnholdFn,
  dispatchComment,
  navCompany,
  failureRequests,
  resetErrorFn,
}) => {
  const theme = useTheme();
  const pc = useDefaultPasscodeStyles();
  const sx = useMemo(
    () => ({
      centerer: {
        display: "flex",
        justifyContent: "center",
      },
      button: {
        width: "175px",
        m: theme.spacing(2, 1, 1, 1),
      },
    }),
    [theme]
  );
  const [documentNumbersCount, setDocumentNumbersCount] = useState(0);
  const [makeRequest, setMakeRequest] = useState(false);
  const [onholdRequest, setOnholdRequest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (makeRequest && documentNumbersCount < documentNumbers.length)
        await sendingDocumentNumbersFn(navCompany, documentNumbers[documentNumbersCount], testEmail !== "" ? testEmail : null);
      if (onholdRequest && documentNumbersCount < documentNumbers.length)
        await setDocumentNumberOnholdFn(navCompany, documentNumbers[documentNumbersCount]);
      if (makeRequest || onholdRequest) setDocumentNumbersCount(documentNumbersCount + 1);
    };
    if (documentNumbersCount === documentNumbers.length || failureRequests[0] === documentNumbers[0]) {
      setMakeRequest(false);
      setOnholdRequest(false);
    }
    if (failureRequests[0] === documentNumbers[0] && failureRequests.length < 0) {
      setErrorMessage(
        "Es scheint als könnten sie den Billing-Service nicht erreichen. Oder es gibt einen Fehler bei der ersten Rechnung"
      );
      return;
    }
    fetchData();
  }, [
    makeRequest,
    onholdRequest,
    documentNumbersCount,
    failureRequests,
    setDocumentNumbersCount,
    documentNumbers,
    sendingDocumentNumbersFn,
    setDocumentNumberOnholdFn,
    testEmail,
    navCompany,
  ]);

  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  return [
    <Box key="buttonbar-wrapper" sx={sx.centerer}>
      <div key="buttonbar">
        <TextField
          key="send-document-test-mail"
          label="Test E-Mail"
          className=""
          value={testEmail}
          onChange={event => setTestEmail(event.target.value)}
          margin="normal"
          disabled={makeRequest}
        />
        <Button
          key="send-document-button"
          variant="contained"
          sx={sx.button}
          onClick={() => {
            if (dispatchComment === "") {
              setDocumentNumbersCount(0);
              resetErrorFn();
              setMakeRequest(true);
            } else {
              setOpen(true);
            }
          }}
          disabled={onholdRequest || makeRequest}
        >
          Send Documents
        </Button>
        <Dialog
          key="send-document-with-dispatchComment-dialog"
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Belegversandposten versenden:</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Beim versenden wird der gesetzte &quot;{dispatchComment}&quot; Status (eventuell) aufgehoben.
            </DialogContentText>
            <DialogContentText id="alert-dialog-question">
              Möchten Sie die {documentNumbers.length} Belegversandposten mit der Versandbemerkung &quot;
              {dispatchComment}&quot; wirklich versenden?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button key="dialog-cancel-button" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button
              key="dialog-execuite-button"
              autoFocus
              onClick={() => {
                setDocumentNumbersCount(0);
                resetErrorFn();
                setMakeRequest(true);
                handleClose();
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
        {dispatchComment !== "ONHOLD" && (
          <Button
            key="set-onhold-button"
            variant="contained"
            sx={sx.button}
            onClick={() => {
              setDocumentNumbersCount(0);
              resetErrorFn();
              setOnholdRequest(true);
            }}
            disabled={onholdRequest || makeRequest}
          >
            {translate.onHold}
          </Button>
        )}
        <Box key="buttonbar-linebreak-1" sx={pc.lineBreak} />
        {documentNumbersCount < documentNumbers.length && (makeRequest || onholdRequest) && (
          <div key="progress-bar">
            <div key="progress-bar-message">Sending Document Number: {documentNumbers[documentNumbersCount]}</div>
            <LinearProgress variant="determinate" value={(documentNumbersCount / documentNumbers.length) * 100} />
            <Button
              key="stop-button"
              variant="contained"
              sx={sx.button}
              onClick={() => {
                setMakeRequest(false);
                setOnholdRequest(false);
              }}
            >
              STOP!
            </Button>
          </div>
        )}
        {failureRequests.map(failure => (
          <div key="progress-error">Problem on Sending Document Number: {failure}</div>
        ))}
        {documentNumbersCount === documentNumbers.length && <div key="progress-success">All Jobs Done!</div>}
      </div>
    </Box>,
    <ErrorModal
      key="error"
      handleCloseErrorModal={() => setErrorMessage("")}
      openErrorModal={errorMessage !== ""}
      errorMessage={errorMessage}
    />,
  ];
};

BillingDocumentSendingEventComponent.proptypes = {
  documentNumbers: Proptypes.array.isRequired,
  errorMessage: Proptypes.string.isRequired,
  sendingDocumentNumbersFn: Proptypes.func.isRequired,
  setDocumentNumberOnholdFn: Proptypes.func.isRequired,
  dispatchComment: Proptypes.string.isRequired,
  navCompany: Proptypes.object.isRequired,
  translate: Proptypes.object.isRequired,
  failureRequests: Proptypes.array.isRequired,
  resetErrorFn: Proptypes.func.isRequired,
};

const mapStateToProps = state => ({
  documentNumbers: state.billing.documentNumbers,
  navCompany: state.billing.navCompany,
  dispatchComment: state.billing.documentFilter.dispatchComment,
  translate: state.ui.translate,
  failureRequests: state.billing.failureRequests,
});

const mapDispatchToProps = dispatch => ({
  sendingDocumentNumbersFn: async (navCompany, id, sendToTestAddress) => {
    await dispatch(sendingDocumentNumbers(navCompany.key, id, sendToTestAddress));
  },
  setDocumentNumberOnholdFn: async (navCompany, id) => {
    await dispatch(sendingDocumentNumberOnhold(navCompany.key, id));
  },
  resetErrorFn: () => dispatch(resetFailures()),
});

const BillingDocumentSendingEvent = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingDocumentSendingEventComponent);

export default BillingDocumentSendingEvent;
